import type { Inserat, InseratStatus } from "@/api/inserate";

/**
 * Filter state used by the Inserate triage view.
 *
 * - `status` and `quelle` are sent to the API (server-side filter).
 * - `stadt`, price- and €/m² ranges are applied client-side after fetching,
 *   so the user can tweak them without round-trips.
 */
export interface InserateFilters {
  status: InseratStatus | "all";
  quelle: string;
  stadt: string;
  preisMin: number | null;
  preisMax: number | null;
  preisProM2Min: number | null;
  preisProM2Max: number | null;
}

export const EMPTY_FILTERS: InserateFilters = {
  status: "all",
  quelle: "",
  stadt: "",
  preisMin: null,
  preisMax: null,
  preisProM2Min: null,
  preisProM2Max: null,
};

/** Apply the *client-side* portion of the filters (everything except `status`
 * + `quelle`, which are server-side). Pure function — easy to unit-test. */
export function applyClientFilters(
  rows: Inserat[],
  f: InserateFilters,
): Inserat[] {
  const stadt = f.stadt.trim().toLowerCase();

  return rows.filter((r) => {
    if (stadt) {
      const ort = (r.adresse.ort ?? "").toLowerCase();
      if (!ort.includes(stadt)) return false;
    }

    if (f.preisMin != null) {
      if (r.kaufpreis_eur == null || r.kaufpreis_eur < f.preisMin) return false;
    }
    if (f.preisMax != null) {
      if (r.kaufpreis_eur == null || r.kaufpreis_eur > f.preisMax) return false;
    }

    if (f.preisProM2Min != null || f.preisProM2Max != null) {
      const ppm =
        r.kaufpreis_eur != null &&
        r.wohnflaeche_m2 != null &&
        r.wohnflaeche_m2 > 0
          ? r.kaufpreis_eur / r.wohnflaeche_m2
          : null;
      if (ppm == null) return false;
      if (f.preisProM2Min != null && ppm < f.preisProM2Min) return false;
      if (f.preisProM2Max != null && ppm > f.preisProM2Max) return false;
    }

    return true;
  });
}

/** Convert filter state to API params (only the server-side bits). */
export function toApiParams(f: InserateFilters) {
  const params: { status_filter?: InseratStatus; quelle?: string } = {};
  if (f.status !== "all") params.status_filter = f.status;
  if (f.quelle.trim()) params.quelle = f.quelle.trim();
  return params;
}
